import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

import PageLayout from '../../layouts/PageLayout';
import CreateTask from '../../components/Task/CreateTask';
import EmployeeTaskTracker from '../../components/Task/Employee/EmployeeTaskTracker';
import TimeUtilization from '../../components/Task/Employee/TimeUtilization';
import {
  useLazyGetEmployeeTimeTrackingQuery,
  useCreateTaskMutation,
  useUpdateTaskMutation,
  useLazyGetTimeUtilizationDataQuery,
} from '../../features/apis/taskManager';

import ReactToastify from '../../hooks/useReactToastify';
import { ActionButton } from '../../components/Dashboard';

const EmployeeTaskDetails: React.FC = () => {
  const { employeeId } = useParams<{ employeeId: string }>();

  const [isModalOpen, setIsModalOpen] = useState(false);

  const [
    getUserTasks,
    {
      data: timeTrackingData,
      isLoading: isTrackingLoading,
      isFetching: isTrackingFetching,
    },
  ] = useLazyGetEmployeeTimeTrackingQuery();

  const [
    getTimeUtilizationData,
    {
      data: utilizationData,
      isLoading: isUtilizationLoading,
      isFetching: isUtilizationFetching,
    },
  ] = useLazyGetTimeUtilizationDataQuery();

  const [createTask] = useCreateTaskMutation();
  const [updateTask] = useUpdateTaskMutation();

  const [isSaving, setIsSaving] = useState(false);

  const isTrackingBusy = isTrackingLoading || isTrackingFetching;
  const isUtilizationBusy =
    isUtilizationLoading || isUtilizationFetching;

  useEffect(() => {
    if (!employeeId) return;

    const fetchTasks = async () => {
      try {
        await getUserTasks(employeeId).unwrap();
      } catch (err) {
        console.log('Error fetching tasks:', err);
      }
    };

    fetchTasks();
  }, [employeeId]);
  useEffect(() => {
    if (!employeeId) return;

    const fetchUtilization = async () => {
      try {
        await getTimeUtilizationData(employeeId).unwrap();
      } catch (err) {
        console.log('Error fetching utilization:', err);
      }
    };

    fetchUtilization();
  }, [employeeId]);
  const handleSaveTask = async (taskData: any) => {
    if (!employeeId) return;

    setIsSaving(true);

    try {
      if (taskData._id) {
        await updateTask(taskData).unwrap();
      } else {
        await createTask({
          ...taskData,
          employeeId,
        }).unwrap();
      }

      await getUserTasks(employeeId).unwrap();
      await getTimeUtilizationData(employeeId).unwrap();

      ReactToastify({
        success: true,
        message: 'Task saved successfully',
      });

      setIsModalOpen(false);
    } catch (err) {
      console.log('Error saving task:', err);

      ReactToastify({
        success: false,
        message: 'Error saving task',
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <PageLayout
      pageTitle="My Tasks"
      action2={
        <ActionButton
          className="bg-gradient-to-r from-[#6535EB] to-[#9F3198] flex items-center justify-center p-2 font-inter rounded-md text-white gap-2 hover:scale-105 hover:shadow text-sm"
          onClick={() => setIsModalOpen(true)}
        >
          + Add Task
        </ActionButton>
      }
    >
      <div className="w-full h-full px-6 py-10">
        <EmployeeTaskTracker
          data={timeTrackingData}
          isLoading={isTrackingBusy}
        />

        <TimeUtilization
          data={utilizationData}
          isLoading={isUtilizationBusy}
          refetch={() => getTimeUtilizationData(employeeId!).unwrap()}
        />
      </div>

      <CreateTask
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSubmit={handleSaveTask}
        isLoading={isSaving}
        selectedDate={new Date()}
        isEmployee={true}
      />
    </PageLayout>
  );
};

export default EmployeeTaskDetails;
